import { sdkEvents } from '../core/events.js';
import { _finishHttpPerf, _startHttpPerf } from '../devtool/perf-counters.js';
import { newCorrelationId } from '../errors/correlation.js';
import { hashDiagnosticEntity } from '../tx-engine/diagnostics/lifecycle.js';

export type HttpRequestOutcome = 'success' | 'http_error' | 'network_error' | 'cancelled';

export interface HttpRequestEvent {
  readonly operationName: string;
  readonly routeTemplate: string;
  readonly requestKind: 'query' | 'mutation';
  readonly method: string;
  readonly outcome: HttpRequestOutcome;
  readonly retryCount: number;
  readonly correlationId: string;
  readonly durationMs: number;
  readonly statusCode?: number;
  readonly requestId?: string;
  readonly network?: string;
  readonly liveEvent?: {
    readonly eventId?: string;
    readonly batchId?: string;
    readonly page: 'initial' | 'cursor';
    readonly pageId?: string;
  };
}

export function subscribeHttpRequests(listener: (event: HttpRequestEvent) => void): () => void {
  return sdkEvents.on('http:request', (payload) => {
    const event = payload as HttpRequestEvent;
    try {
      listener(event);
    } catch {
      // A diagnostic sink must not change the response or thrown transport error.
      console.warn('sdk.http_diagnostic_sink_failed', {
        operationName: event.operationName,
        correlationId: event.correlationId,
        outcome: event.outcome,
        retryCount: event.retryCount,
        durationMs: event.durationMs,
        statusCode: event.statusCode,
      });
    }
  });
}

export interface HttpRequestTelemetry {
  readonly operationName: string;
  readonly routeTemplate: string;
  readonly requestKind: 'query' | 'mutation';
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly url: string;
  readonly init: RequestInit;
  readonly signal?: AbortSignal;
  readonly retryCount?: number;
}

const attemptsBySignal = new WeakMap<AbortSignal, number>();
const responseStatusBySignal = new WeakMap<AbortSignal, number>();
let diagnosticNetwork: string | undefined;

/** Mirrors the SDK's single installed fetch configuration; captured per request. */
export function setHttpTelemetryNetwork(network: string): void {
  diagnosticNetwork = ['local', 'devnet', 'mainnet'].includes(network) ? network : undefined;
}

/** Preserve soft 401 responses for scheduling without changing anonymous hook data. */
export function getHttpResponseStatus(signal: AbortSignal | undefined): number | undefined {
  return signal === undefined ? undefined : responseStatusBySignal.get(signal);
}

/**
 * Fetch a Morfi API route and emit exactly one privacy-safe HTTP telemetry
 * event for the attempt. Concrete URLs and query values stay local; only the
 * manifest route template reaches the telemetry bus.
 */
export async function fetchWithHttpTelemetry(input: HttpRequestTelemetry): Promise<Response> {
  const network = diagnosticNetwork;
  const retryCount = input.retryCount ?? nextRetryCount(input.signal);
  const headers = new Headers(input.init.headers);
  const correlationId = headers.get('x-correlation-id') ?? newCorrelationId();
  headers.set('x-correlation-id', correlationId);
  const startedAt = Date.now();
  let outcome: HttpRequestOutcome | undefined;
  if (process.env.NODE_ENV !== 'production') {
    _startHttpPerf();
  }

  try {
    const response = await fetch(input.url, { ...input.init, headers });
    const requestId = safeRequestId(response.headers.get('x-request-id'));
    if (input.signal !== undefined) responseStatusBySignal.set(input.signal, response.status);
    outcome = response.ok ? 'success' : 'http_error';
    emitHttpRequest(input, {
      network,
      correlationId,
      durationMs: Date.now() - startedAt,
      outcome,
      retryCount,
      statusCode: response.status,
      requestId,
    });
    if (response.ok) clearAttempts(input.signal);
    return response;
  } catch (error: unknown) {
    outcome = isCancellation(error, input.signal) ? 'cancelled' : 'network_error';
    emitHttpRequest(input, {
      network,
      correlationId,
      durationMs: Date.now() - startedAt,
      outcome,
      retryCount,
    });
    if (outcome === 'cancelled') clearAttempts(input.signal);
    throw error;
  } finally {
    if (process.env.NODE_ENV !== 'production') {
      _finishHttpPerf(outcome === 'cancelled');
    }
  }
}

interface HttpRequestResult {
  readonly network: string | undefined;
  readonly correlationId: string;
  readonly durationMs: number;
  readonly outcome: HttpRequestOutcome;
  readonly retryCount: number;
  readonly statusCode?: number;
  readonly requestId?: string | undefined;
}

function emitHttpRequest(input: HttpRequestTelemetry, result: HttpRequestResult): void {
  const event = {
    method: input.method,
    operationName: input.operationName,
    routeTemplate: input.routeTemplate,
    requestKind: input.requestKind,
    outcome: result.outcome,
    retryCount: result.retryCount,
    correlationId: result.correlationId,
    durationMs: result.durationMs,
    ...liveEventContext(input),
  };
  if (result.network !== undefined) Object.assign(event, { network: result.network });
  if (result.statusCode !== undefined) Object.assign(event, { statusCode: result.statusCode });
  if (result.requestId !== undefined) Object.assign(event, { requestId: result.requestId });
  sdkEvents.emit('http:request', event);
}

function liveEventContext(input: HttpRequestTelemetry): Pick<HttpRequestEvent, 'liveEvent'> {
  if (
    !['useGetEvent', 'useGetEvents', 'useGetMarkets', 'useGetProposals'].includes(
      input.operationName,
    )
  )
    return {};
  let url: URL;
  try {
    url = new URL(input.url, 'https://sdk.invalid');
  } catch {
    // Invalid request URLs retain the original fetch failure and telemetry.
    return {};
  }
  return liveEventIdentity(input.operationName, url);
}

function liveEventIdentity(operationName: string, url: URL): Pick<HttpRequestEvent, 'liveEvent'> {
  const pageContext = liveEventPage(url.searchParams.get('cursor'));
  if (operationName === 'useGetEvents') return { liveEvent: pageContext };
  if (operationName === 'useGetProposals') {
    const pubkeys = url.searchParams.get('pubkeys');
    return pubkeys === null
      ? {}
      : { liveEvent: { ...pageContext, batchId: hashDiagnosticEntity(pubkeys) } };
  }
  const eventId =
    operationName === 'useGetEvent'
      ? url.pathname.split('/').at(-1)
      : url.searchParams.get('eventId');
  return typeof eventId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(eventId)
    ? { liveEvent: { ...pageContext, eventId } }
    : {};
}

function liveEventPage(cursor: string | null): NonNullable<HttpRequestEvent['liveEvent']> {
  return cursor === null
    ? { page: 'initial' }
    : { page: 'cursor', pageId: hashDiagnosticEntity(cursor) };
}

function safeRequestId(value: string | null): string | undefined {
  return value !== null && /^(?:[0-9a-f]{8}-[0-9a-f-]{27}|[0-9A-HJKMNP-TV-Z]{26})$/i.test(value)
    ? value
    : undefined;
}

function nextRetryCount(signal: AbortSignal | undefined): number {
  if (signal === undefined) return 0;
  const retryCount = attemptsBySignal.get(signal) ?? 0;
  attemptsBySignal.set(signal, retryCount + 1);
  return retryCount;
}

function clearAttempts(signal: AbortSignal | undefined): void {
  if (signal !== undefined) attemptsBySignal.delete(signal);
}

function isCancellation(error: unknown, signal: AbortSignal | undefined): boolean {
  if (signal?.aborted === true) return true;
  return error instanceof Error && error.name === 'AbortError';
}
